/**
 * GAP-3 (docs/cloud-vm/gui-parity-plan.md Phase 2) — pure helpers for the
 * Workspace chat-hydration bounded-retry-with-backoff policy.
 *
 * Extracted from the `Workspace.tsx` mount effect so the retry DECISION
 * (retry vs give up) and the BACKOFF SCHEDULE are unit-testable without
 * mounting React or driving fake timers through the whole component. The
 * effect stays the thin orchestration layer (await + dispatch + cancel
 * guard); the policy lives here.
 *
 * Why a policy at all: before GAP-3 the mount effect ran `chat:load` exactly
 * once. On the cloud path a transient WSS blip during hydration left the chat
 * surface with an empty `messages` array and a banner — an indistinguishable
 * permanent blank with no way back short of a full reload. Bounded retry turns
 * the common transient failure into a self-healing load.
 */

import type { SerialisedError } from '@volley/crucible-ipc-types/error-types';

/**
 * Max number of hydration ATTEMPTS (the initial try + retries) for a
 * retryable transport error. Kept small + bounded so a genuinely-down pod
 * surfaces an error state quickly rather than spinning forever. After this
 * many failed attempts the effect dispatches `chat/hydration-failed`.
 */
export const MAX_HYDRATION_ATTEMPTS = 3;

/**
 * Base backoff in ms. Backoff grows exponentially per retry
 * (`BASE * 2^(retryIndex)`) and is capped at {@link MAX_HYDRATION_BACKOFF_MS}.
 * Deliberately short: this is a foreground load the user is staring at, not a
 * background sync. The first retry waits ~400ms, the second ~800ms.
 */
export const BASE_HYDRATION_BACKOFF_MS = 400;

/** Upper bound on a single backoff wait. */
export const MAX_HYDRATION_BACKOFF_MS = 4000;

/**
 * How long a single hydration load may stay pending before the surface
 * upgrades the quick "Loading chat history…" affordance to the explicit
 * "preparing your workspace" message. The cloud first-touch `chat.load@1`
 * can block on a git clone for up to ~120s; this threshold is the point past
 * which a blank window should read as long-running provisioning, not a bug.
 */
export const HYDRATION_PREPARING_THRESHOLD_MS = 4000;

/**
 * Is this hydration failure worth retrying?
 *
 * Retry ONLY when the serialised error self-declares `retryable: true` AND it
 * is not corrupt-history. Corrupt-history is a `CrucibleError` with category
 * `'state'` (CHAT-CORRUPT / CHAT-SHAPE): per the plan it must stay a one-shot
 * recoverable banner pointing at the backup file — retrying just re-reads the
 * same corrupt bytes and burns the budget. A plain throw (`retryable: false`,
 * category `'unknown'`) is also not retried; we don't know it's safe to.
 *
 * Mirrors the existing renderer error classification: `unflattenIpcError`
 * produces the `SerialisedError` whose `retryable` / `category` we read here.
 */
export function isRetryableHydrationError(error: SerialisedError): boolean {
  if (error.category === 'state') return false;
  return error.retryable === true;
}

/**
 * Should the effect attempt another load after a failure on a given attempt?
 *
 * @param error           the failure that just occurred (already unflattened)
 * @param attemptsSoFar   number of attempts completed so far (1 after the
 *                        first failure, 2 after the second, …)
 * @returns               true iff the error is retryable AND we are still
 *                        under {@link MAX_HYDRATION_ATTEMPTS}
 */
export function shouldRetryHydration(error: SerialisedError, attemptsSoFar: number): boolean {
  if (attemptsSoFar >= MAX_HYDRATION_ATTEMPTS) return false;
  return isRetryableHydrationError(error);
}

/**
 * Backoff (ms) to wait before retry number `retryIndex` (0-based: the wait
 * before the FIRST retry is `retryIndex = 0`). Exponential, capped.
 */
export function hydrationBackoffMs(retryIndex: number): number {
  const raw = BASE_HYDRATION_BACKOFF_MS * 2 ** Math.max(0, retryIndex);
  return Math.min(raw, MAX_HYDRATION_BACKOFF_MS);
}
